import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking
  const [ready, setReady] = useState(false);

  useEffect(() => {
    ["quick_username", "device_tokens"].forEach((k) => localStorage.removeItem(k));
    const token = localStorage.getItem("token");
    if (!token) {
      setUser(false);
      setReady(true);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => {
        localStorage.removeItem("token");
        setUser(false);
      })
      .finally(() => setReady(true));
  }, []);

  const persist = async (data) => {
    if (data.access_token) localStorage.setItem("token", data.access_token);
    setUser(data.user);
    try { const me = await api.get("/auth/me"); setUser(me.data); return me.data; } catch { return data.user; }
  };

  const login = async (identifier, password) => {
    const res = await api.post("/auth/login", { identifier, password });
    return persist(res.data);
  };

  const register = async (name, username, email, password) => {
    const res = await api.post("/auth/register", { name, username, email, password });
    return persist(res.data);
  };

  const logout = () => {
    localStorage.removeItem("token");
    setUser(false);
  };

  const refreshUser = async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      return res.data;
    } catch {
      return null;
    }
  };

  const value = useMemo(
    () => ({ user, ready, login, register, logout, refreshUser }),
    // login/register/logout are stable closures over setUser; only re-memo on auth state
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, ready]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
